// =============================================================================
// MeuDriver — LessonHistoryTimeline
// Linha do tempo de aulas concluídas com data, instrutor e nota de evolução
// =============================================================================

import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Calendar, User, FileText, Star } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import { getInitials } from '@/lib/utils'
import type { LessonHistoryEntry } from '@/hooks/useStudentProgress'

interface Props {
  entries: LessonHistoryEntry[]
  isLoading?: boolean
  limit?: number
}

const SKILL_KEYS = [
  'skill_partida', 'skill_direcao', 'skill_freios', 'skill_marchas', 'skill_baliza',
  'skill_garagem', 'skill_retorno', 'skill_via_rapida', 'skill_noturna', 'skill_comportamento',
] as const

function avgSkill(sr: LessonHistoryEntry['skill_rating']): number | null {
  if (!sr) return null
  const vals = SKILL_KEYS.map((k) => (sr as any)[k] as number | null).filter((v) => v !== null)
  if (vals.length === 0) return null
  return vals.reduce((a, b) => a + b!, 0) / vals.length
}

function SkillDots({ avg }: { avg: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`h-3 w-3 ${n <= Math.round(avg) ? 'fill-amber-400 text-amber-400' : 'fill-gray-100 text-gray-200'}`}
        />
      ))}
      <span className="ml-1 text-xs text-muted-foreground">{avg.toFixed(1)}/5</span>
    </div>
  )
}

export function LessonHistoryTimeline({ entries, isLoading, limit = 10 }: Props) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex gap-4">
            <Skeleton className="h-10 w-10 rounded-full shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-48" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (entries.length === 0) {
    return (
      <div className="text-center py-10 text-muted-foreground text-sm">
        Nenhuma aula concluída ainda.
      </div>
    )
  }

  const visible = entries.slice(0, limit)

  return (
    <div className="relative" data-testid="lesson-history-timeline">
      {/* Linha vertical */}
      <div className="absolute left-5 top-0 bottom-0 w-px bg-gray-100" />

      <div className="space-y-5">
        {visible.map((entry) => {
          const avg = avgSkill(entry.skill_rating)
          return (
            <div key={entry.id} className="relative flex gap-4">
              {/* Avatar com linha */}
              <Avatar className="h-10 w-10 shrink-0 ring-2 ring-white shadow-sm z-10">
                <AvatarImage src={entry.instructor_avatar ?? undefined} />
                <AvatarFallback className="bg-brand-100 text-brand-700 text-xs font-semibold">
                  {entry.instructor_name ? getInitials(entry.instructor_name) : <User className="h-4 w-4" />}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 min-w-0 pb-1">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                  <p className="font-semibold text-sm text-gray-900 truncate">
                    {entry.instructor_name ?? 'Instrutor'}
                  </p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {format(parseISO(entry.scheduled_at), "d 'de' MMM yyyy, HH'h'mm", { locale: ptBR })}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {entry.duration_minutes} min
                  </p>
                </div>

                {avg !== null && (
                  <div className="mt-1">
                    <SkillDots avg={avg} />
                  </div>
                )}

                {entry.instructor_note && (
                  <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
                    <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground mt-0.5" />
                    <p className="text-xs text-gray-600 leading-relaxed">{entry.instructor_note}</p>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
